import { useEffect, useState } from 'react'
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  FolderIcon,
  MegaphoneIcon,
  ChatBubbleLeftRightIcon,
  BookOpenIcon,
  LightBulbIcon,
  QuestionMarkCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import { Card, Badge, InlineSpinner, EmptyState, Button, Modal, ModalBody, ModalFooter } from '@/components/ui'
import { forumApi } from '@/api'
import type { AdminForumCategory, CreateCategoryData } from '@/api/forum'

// Map icon names to Heroicons components
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  'folder': FolderIcon,
  'megaphone': MegaphoneIcon,
  'message-circle': ChatBubbleLeftRightIcon,
  'book-open': BookOpenIcon,
  'lightbulb': LightBulbIcon,
  'help-circle': QuestionMarkCircleIcon,
  'users': UserGroupIcon,
}

const iconOptions = [
  { value: 'folder', label: 'Cartella' },
  { value: 'megaphone', label: 'Annunci' },
  { value: 'message-circle', label: 'Discussioni' },
  { value: 'book-open', label: 'Guide' },
  { value: 'lightbulb', label: 'Idee' },
  { value: 'help-circle', label: 'Supporto' },
  { value: 'users', label: 'Community' },
]

const roleOptions = [
  { value: 'guest', label: 'Ospite' },
  { value: 'member', label: 'Membro' },
  { value: 'premium_member', label: 'Premium' },
  { value: 'expert', label: 'Esperto' },
  { value: 'community_manager', label: 'Community Manager' },
  { value: 'admin', label: 'Admin' },
]

const getIconComponent = (iconName: string) => {
  return iconMap[iconName] || FolderIcon
}

const emptyForm: CreateCategoryData = {
  name: '',
  slug: '',
  description: '',
  icon: 'folder',
  order: 0,
  is_official: false,
  is_active: true,
  requires_approval: false,
  min_role: 'member',
}

export default function AdminForumPage() {
  const [categories, setCategories] = useState<AdminForumCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')

  // Create/Edit Modal
  const [showModal, setShowModal] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<AdminForumCategory | null>(null)
  const [form, setForm] = useState<CreateCategoryData>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [categoryToDelete, setCategoryToDelete] = useState<AdminForumCategory | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    fetchCategories()
  }, [])

  const fetchCategories = async () => {
    setLoading(true)
    try {
      const data = await forumApi.admin.getCategories()
      setCategories(data)
    } catch (error) {
      console.error('Error fetching categories:', error)
    } finally {
      setLoading(false)
    }
  }

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
  }

  const handleNameChange = (name: string) => {
    setForm({
      ...form,
      name,
      slug: isEditing ? form.slug : generateSlug(name),
    })
  }

  const handleOpenCreate = () => {
    setIsEditing(false)
    setSelectedCategory(null)
    setForm(emptyForm)
    setError(null)
    setShowModal(true)
  }

  const handleOpenEdit = (category: AdminForumCategory) => {
    setIsEditing(true)
    setSelectedCategory(category)
    setForm({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      icon: category.icon,
      order: category.order,
      is_official: category.is_official,
      is_active: category.is_active,
      requires_approval: category.requires_approval,
      min_role: category.min_role,
    })
    setError(null)
    setShowModal(true)
  }

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Il nome è obbligatorio')
      return
    }
    if (!form.slug.trim()) {
      setError('Lo slug è obbligatorio')
      return
    }

    setSaving(true)
    setError(null)

    try {
      if (isEditing && selectedCategory) {
        await forumApi.admin.updateCategory(selectedCategory.id, form)
      } else {
        await forumApi.admin.createCategory(form)
      }
      setShowModal(false)
      setForm(emptyForm)
      fetchCategories()
    } catch (err: unknown) {
      const error = err as { response?: { data?: Record<string, string[]> } }
      if (error.response?.data) {
        const messages = Object.values(error.response.data).flat()
        setError(messages.join(', '))
      } else {
        setError('Errore durante il salvataggio')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!categoryToDelete) return
    setDeleting(true)
    try {
      await forumApi.admin.deleteCategory(categoryToDelete.id)
      setShowDeleteModal(false)
      setCategoryToDelete(null)
      fetchCategories()
    } catch (error) {
      console.error('Error deleting category:', error)
    } finally {
      setDeleting(false)
    }
  }

  const filteredCategories = categories.filter(
    (cat) =>
      cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cat.slug.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (loading) {
    return <InlineSpinner message="Caricamento categorie..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Categorie Forum</h1>
          <p className="text-gray-600">{categories.length} categorie configurate</p>
        </div>
        <Button onClick={handleOpenCreate}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Nuova Categoria
        </Button>
      </div>

      {/* Search */}
      <Card>
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca per nome o slug..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </Card>

      {/* Categories Table */}
      {filteredCategories.length > 0 ? (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Categoria
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Slug
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statistiche
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stato
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ordine
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredCategories.map((category) => {
                  const IconComponent = getIconComponent(category.icon)
                  return (
                    <tr key={category.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                            <IconComponent className="h-5 w-5 text-primary-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{category.name}</p>
                            <p className="text-sm text-gray-500 truncate max-w-xs">
                              {category.description || 'Nessuna descrizione'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                          {category.slug}
                        </code>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        <div>{category.threads_count || 0} discussioni</div>
                        <div>{category.posts_count || 0} messaggi</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-1">
                          {category.is_active ? (
                            <Badge variant="success">Attiva</Badge>
                          ) : (
                            <Badge variant="default">Disattivata</Badge>
                          )}
                          {category.is_official && (
                            <Badge variant="info">Ufficiale</Badge>
                          )}
                          {category.requires_approval && (
                            <Badge variant="warning">Moderata</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {category.order}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleOpenEdit(category)}
                            className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                            title="Modifica"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => {
                              setCategoryToDelete(category)
                              setShowDeleteModal(true)
                            }}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                            title="Elimina"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card>
          <EmptyState
            icon={<FolderIcon className="h-12 w-12" />}
            title={searchQuery ? 'Nessuna categoria trovata' : 'Nessuna categoria'}
            description={
              searchQuery
                ? 'Prova a modificare i termini di ricerca'
                : 'Crea la prima categoria per il forum'
            }
            action={
              !searchQuery
                ? { label: 'Crea Categoria', onClick: handleOpenCreate }
                : undefined
            }
          />
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={isEditing ? 'Modifica Categoria' : 'Nuova Categoria'}
      >
        <ModalBody>
          <div className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="es. Annunci Ufficiali"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Slug *
              </label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="es. annunci-ufficiali"
              />
              <p className="mt-1 text-xs text-gray-500">
                Usato nell'URL: /forum/category/{form.slug || 'slug'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrizione
              </label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                rows={3}
                placeholder="Descrizione della categoria..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Icona
                </label>
                <select
                  value={form.icon}
                  onChange={(e) => setForm({ ...form, icon: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  {iconOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ordine
                </label>
                <input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ruolo minimo per accesso
              </label>
              <select
                value={form.min_role}
                onChange={(e) => setForm({ ...form, min_role: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {roleOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Attiva</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_official}
                  onChange={(e) => setForm({ ...form, is_official: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Categoria ufficiale</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.requires_approval}
                  onChange={(e) => setForm({ ...form, requires_approval: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Richiede approvazione post</span>
              </label>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowModal(false)}>
            Annulla
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Salvataggio...' : isEditing ? 'Salva modifiche' : 'Crea categoria'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Elimina Categoria"
      >
        <ModalBody>
          {categoryToDelete && (
            <div className="space-y-4">
              <p className="text-gray-600">
                Sei sicuro di voler eliminare la categoria{' '}
                <span className="font-semibold">{categoryToDelete.name}</span>?
              </p>
              {(categoryToDelete.threads_count || 0) > 0 && (
                <p className="text-sm text-red-600">
                  Attenzione: questa categoria contiene {categoryToDelete.threads_count} discussioni
                  che verranno eliminate.
                </p>
              )}
              <p className="text-sm text-gray-500">
                Questa azione è irreversibile.
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
            Annulla
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Eliminazione...' : 'Elimina categoria'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
